import PrettyDate from '@/components/PrettyDate';
import {
  fetchLeadMailMessage,
  fetchLeadThreadMessages,
  getLeadMailAttachmentUrl,
} from '@/services/api/email';
import {
  MailAttachment,
  MailMessage,
  MailThread,
} from '@/typings/models/Email';
import { F } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  DownloadOutlined,
  PaperClipOutlined,
  RollbackOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Avatar,
  Button,
  Collapse,
  Empty,
  List,
  Skeleton,
  Space,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'umi';
import styles from './EmailViewer.less';

const { Text } = Typography;

const recipientList = (
  recipients?: { emailAddress: { address: string; name?: string } }[],
) =>
  (recipients || [])
    .map((r) => r.emailAddress.name || r.emailAddress.address)
    .join(', ');

/** "Sajad Shahi" → "SS"; falls back to the address's first two letters. */
const senderInitials = (message: MailMessage) => {
  const name = message.from?.emailAddress?.name;
  if (name) {
    const parts = name.trim().split(/\s+/);
    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
  return (message.from?.emailAddress?.address || '?').slice(0, 2).toUpperCase();
};

interface MessageBodyProps {
  leadId: string;
  mailbox: string;
  message: MailMessage;
}

/**
 * Full message body, fetched live from Graph on expand and rendered in a
 * sandboxed iframe — same isolation as Communication/renderers/Email. The
 * frame sizes itself to its content after load (fixed heights either bury a
 * one-line mail in blank space or clip a long one behind a nested scrollbar);
 * the measured value travels through a CSS variable to keep styling in LESS.
 */
const MessageBody: React.FC<MessageBodyProps> = ({
  leadId,
  mailbox,
  message,
}) => {
  const intl = useIntl();
  const [html, setHtml] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<MailAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [frameHeight, setFrameHeight] = useState<number | null>(null);

  useEffect(() => {
    fetchLeadMailMessage(leadId, message.id, mailbox).then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess(response: any) {
            setHtml(response.message?.body?.content || '');
            setAttachments(response.attachments || []);
          },
          onEnd() {
            setLoading(false);
          },
        },
        'data',
      );
    });
  }, [leadId, mailbox, message.id]);

  const measureFrame = (event: React.SyntheticEvent<HTMLIFrameElement>) => {
    // allow-same-origin makes the srcdoc document readable from here.
    const body = event.currentTarget.contentDocument?.body;
    if (!body) return;
    // +2 rounding safety only — the body uses padding (included in
    // scrollHeight), so anything more is visible dead space around the text.
    // Cap stays generous: bounce reports with full headers run thousands of
    // px, and a clamped frame grows an inner scrollbar next to the pane's own.
    setFrameHeight(Math.min(Math.max(body.scrollHeight + 2, 40), 6000));
  };

  if (loading) {
    return (
      <Skeleton
        active
        title={false}
        paragraph={{ rows: 3, width: ['90%', '75%', '40%'] }}
        className={styles.bodySkeleton}
      />
    );
  }

  return (
    <div className={styles.messageBody}>
      <iframe
        srcDoc={`
          <style>
            a, button { pointer-events: none !important; cursor: default !important; }
            body {
              /* padding, not margin: padding is part of scrollHeight, so the
                 measured frame height is exact instead of padded twice. */
              margin: 0;
              padding: 6px 0;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              font-size: 13px;
              line-height: 1.55;
              color: #1f2329;
              word-break: break-word;
            }
            img { max-width: 100%; height: auto; }
            blockquote { margin: 8px 0; padding-left: 12px; border-left: 3px solid #d9d9d9; color: #666; }
          </style>
          ${html || ''}
        `}
        title={intl.formatMessage({
          id: 'communication.email.content',
          defaultMessage: 'Email Content',
        })}
        className={styles.messageIframe}
        style={
          frameHeight
            ? ({
                '--mail-frame-height': `${frameHeight}px`,
              } as React.CSSProperties)
            : undefined
        }
        onLoad={measureFrame}
        frameBorder="0"
        width="100%"
        sandbox="allow-same-origin"
      />
      {attachments.length > 0 && (
        <List
          size="small"
          className={styles.attachmentList}
          dataSource={attachments.filter((attachment) => !attachment.isInline)}
          renderItem={(attachment) => (
            <List.Item
              actions={[
                <Button
                  key="download"
                  type="link"
                  size="small"
                  icon={<DownloadOutlined />}
                  href={getLeadMailAttachmentUrl(
                    leadId,
                    message.id,
                    attachment.id,
                    mailbox,
                  )}
                  target="_blank"
                />,
              ]}
            >
              <Space>
                <PaperClipOutlined />
                <Text>{attachment.name}</Text>
                <Text type="secondary">
                  {Math.round(attachment.size / 1024)} KB
                </Text>
              </Space>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

interface EmailViewerProps {
  leadId: string;
  thread: MailThread;
  onReply: (message: MailMessage, replyAll: boolean) => void;
  /** Whether the acting user holds messageThreads_compose — hides reply actions when false. */
  canReply: boolean;
  /** Bumped by the parent after a reply was sent, to refetch the thread. */
  refreshToken?: number;
}

/**
 * One Outlook conversation, fetched live. The newest message starts expanded;
 * bodies load per message on expand. Each message is a card: avatar + sender
 * up top, isolated body below, reply actions on the card itself.
 */
const EmailViewer: React.FC<EmailViewerProps> = ({
  leadId,
  thread,
  onReply,
  canReply,
  refreshToken,
}) => {
  const [messages, setMessages] = useState<MailMessage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetchLeadThreadMessages(
      leadId,
      thread.mailbox_address,
      thread.conversation_id,
    ).then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess(response: any) {
            setMessages(response.messages || []);
          },
          onEnd() {
            setLoading(false);
          },
        },
        'data',
      );
    });
  }, [leadId, thread.mailbox_address, thread.conversation_id, refreshToken]);

  if (loading) {
    // Mirror the loaded layout (header line + a message card with avatar) so
    // the thread doesn't jump when the real content lands.
    return (
      <div className={styles.viewer}>
        <div className={styles.viewerHeader}>
          <Skeleton active title={{ width: '35%' }} paragraph={false} />
        </div>
        <div className={styles.skeletonCard}>
          <Skeleton active avatar={{ size: 'small' }} paragraph={{ rows: 2 }} />
        </div>
      </div>
    );
  }
  if (!messages.length) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  }

  const newestId = messages[messages.length - 1]?.id;

  return (
    <div className={styles.viewer}>
      <div className={styles.viewerHeader}>
        <Text strong className={styles.viewerSubject}>
          {thread.subject || (
            <F id="common.no_subject" defaultMessage="(No Subject)" />
          )}
        </Text>
        <Text type="secondary" className={styles.viewerMeta}>
          <F
            id="email.thread.message_count"
            defaultMessage="{count, plural, one {# message} other {# messages}}"
            values={{ count: messages.length }}
          />
          {' · '}
          <F
            id="email.thread.via_mailbox"
            defaultMessage="via {mailbox}"
            values={{ mailbox: thread.mailbox_address }}
          />
        </Text>
        {/* Every message card shows the same display name, so the header is the
            only place the reader can see who is actually on the thread. The
            addresses are already in the payload (they drive the 'mine' scope
            filter) — no extra request. */}
        {(thread.participants?.length ?? 0) > 0 && (
          <div className={styles.viewerParticipants}>
            <Text type="secondary" className={styles.viewerParticipantsLabel}>
              <F id="email.thread.participants" defaultMessage="People" />
            </Text>
            <span className={styles.viewerParticipantsList}>
              {thread.participants.map((address) => (
                <Tag key={address} className={styles.participantChip}>
                  {address}
                </Tag>
              ))}
            </span>
          </div>
        )}
      </div>
      <Collapse
        ghost
        className={styles.messageCards}
        defaultActiveKey={newestId ? [newestId] : []}
        items={messages.map((message) => ({
          key: message.id,
          label: (
            <div className={styles.messageHeader}>
              <Avatar size="small" className={styles.senderAvatar}>
                {senderInitials(message)}
              </Avatar>
              <div className={styles.messageMeta}>
                <Text strong ellipsis className={styles.senderName}>
                  {message.from?.emailAddress?.name ||
                    message.from?.emailAddress?.address}
                </Text>
                <Text
                  type="secondary"
                  ellipsis
                  className={styles.messageRecipients}
                >
                  <F id="communication.attribute.to" defaultMessage="To:" />{' '}
                  {recipientList(message.toRecipients)}
                </Text>
              </div>
              {message.hasAttachments && (
                <PaperClipOutlined className={styles.headerClip} />
              )}
              <Text type="secondary" className={styles.messageDate}>
                <PrettyDate
                  date={message.sentDateTime || message.receivedDateTime || ''}
                />
              </Text>
            </div>
          ),
          children: (
            <>
              <MessageBody
                leadId={leadId}
                mailbox={thread.mailbox_address}
                message={message}
              />
              {canReply && (
                <Space className={styles.messageActions}>
                  <Button
                    size="small"
                    icon={<RollbackOutlined />}
                    onClick={() => onReply(message, false)}
                  >
                    <F id="email.action.reply" defaultMessage="Reply" />
                  </Button>
                  <Button
                    size="small"
                    icon={<TeamOutlined />}
                    onClick={() => onReply(message, true)}
                  >
                    <F id="email.action.reply_all" defaultMessage="Reply all" />
                  </Button>
                </Space>
              )}
            </>
          ),
        }))}
      />
    </div>
  );
};

export default EmailViewer;
